import React from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { CSSTransition, TransitionGroup } from 'react-transition-group';

import ProtectedRoute from 'components/ProtectedRoute/ProtectedRoute';
import DataLoader from 'store/DataLoader/DataLoader';
import { useSystemSettings } from 'providers/SystemSettingsProvider';
import { useSelector } from 'store';

import LoginPage from './LoginPage/LoginPage';
import ProjectOverviewPage from './ProjectOverviewPage/ProjectOverviewPage';
import TraroElementPage from './TraroElementPage/TraroElementPage';
import Dashboard from './Dashboard/Dashboard';
import KliparoElementPage from './KliparoElementPage/KliparoElementPage';
import TicketsOverviewPage from './TicketsOverviewPage/TicketsOverviewPage';
import TicketPage from './TicketPage/TicketPage';
import RegisterPage from './RegisterPage/RegisterPage';
import ResetPasswordPage from './ResetPasswordPage/ResetPasswordPage';
import CreatePasswordPage from './CreatePasswordPage/CreatePasswordPage';
import OrganizationProfilePage from './OrganizationProfilePage/OrganizationProfilePage';
import ProjectCondensedOverviewPage from './ProjectCondensedOverviewPage/ProjectCondensedOverviewPage';
import SystemDisabledPage from './SystemDisabledPage/SystemDisabledPage';
import UnauthenticatedAccessLoginPage from './UnauthenticatedAccessLoginPage/UnauthenticatedAccessLoginPage';
import ProsforaProjectPage from './ProsforaProjectPage/ProsforaProjectPage';
import DataRoomPage from './DataRoomPage/DataRoomPage';
import ProsforaElementPage from './ProsforaElementPage/ProsforaElementPage';
import ProsforaBidSheet from './ProsforaBidSheet/ProsforaBidSheet';

const Routing = () => {
  const location = useLocation();
  const { enabled, unauthenticated_access, bypass_dashboard_rfp } = useSystemSettings();
  const isLoggedIn = useSelector((store) => store.auth.isLoggedIn);

  if (!enabled) {
    return (
      <Routes>
        <Route path="/disabled" element={<SystemDisabledPage />} />
        <Route path="*" element={<Navigate replace to="/disabled" />} />
      </Routes>
    );
  }

  return (
    <>
      <TransitionGroup>
        <CSSTransition key={location.key} classNames="fade" timeout={300}>
          <Routes>
            {unauthenticated_access ? (
              <Route path="/login" element={<UnauthenticatedAccessLoginPage />} />
            ) : (
              <Route path="/login" element={<LoginPage />} />
            )}
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/create-password/:encodedEmail/:token" element={<CreatePasswordPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route
              path="/dashboard"
              element={
                bypass_dashboard_rfp ? (
                  <Navigate replace to={`/rfp/${bypass_dashboard_rfp}/`} />
                ) : (
                  <ProtectedRoute allowUnauthenticated>
                    <Dashboard />
                  </ProtectedRoute>
                )
              }
            />
            <Route
              path="/tickets"
              element={
                <ProtectedRoute>
                  <TicketsOverviewPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/organization"
              element={
                <ProtectedRoute>
                  <OrganizationProfilePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/ticket/:id"
              element={
                <ProtectedRoute>
                  <TicketPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/rfp/:rfpId/rfp-response/:rfpResponseId/condensed"
              element={
                <ProtectedRoute reviewerOnly>
                  <DataLoader>
                    <ProjectCondensedOverviewPage />
                  </DataLoader>
                </ProtectedRoute>
              }
            />
            <Route
              path="/rfp/:rfpId/rfp-response/:rfpResponseId"
              element={
                <ProtectedRoute>
                  <DataLoader>
                    <ProjectOverviewPage />
                  </DataLoader>
                </ProtectedRoute>
              }
            />
            <Route
              path="/rfp/:rfpId/rfp-response/:rfpResponseId/rfp-element-response/:traroElementSpecific"
              element={
                <ProtectedRoute>
                  <DataLoader>
                    <TraroElementPage />
                  </DataLoader>
                </ProtectedRoute>
              }
            />
            <Route
              path="/rfp/:rfpId/rfp-response/:rfpResponseId/rfp-question-response/:sectionNumber/:questionNumber"
              element={
                <ProtectedRoute>
                  <DataLoader>
                    <KliparoElementPage />
                  </DataLoader>
                </ProtectedRoute>
              }
            />
            <Route
              path="/rfp/:rfpId/data-room"
              element={
                <ProtectedRoute>
                  <DataRoomPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/rfp/:rfpId/bid-sheet"
              element={
                <ProtectedRoute>
                  <DataLoader rfpOnly>
                    <ProsforaBidSheet />
                  </DataLoader>
                </ProtectedRoute>
              }
            />
            <Route
              path="/rfp/:rfpId"
              element={
                <ProtectedRoute allowUnauthenticated>
                  <DataLoader rfpOnly>
                    <ProsforaProjectPage />
                  </DataLoader>
                </ProtectedRoute>
              }
            />
            <Route
              path="/rfp/:rfpId/element/:elementId"
              element={
                <ProtectedRoute>
                  <DataLoader rfpOnly>
                    <ProsforaElementPage />
                  </DataLoader>
                </ProtectedRoute>
              }
            />
            <Route path="*" element={<Navigate replace to={isLoggedIn ? '/dashboard' : '/login'} />} />
          </Routes>
        </CSSTransition>
      </TransitionGroup>
    </>
  );
};

export default Routing;
